// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, computed, observable, observe } from 'mobx';
import { escapeRegExp, filter, uniq, find, findIndex, head, isEmpty, isNil, map } from 'lodash/fp';

import dataFetcher from 'fetchers/Izenda.ConnectionStringDataFetcher';
import uiState from 'logic/Izenda.UiState.js';
import constants from 'logic/data/Izenda.ConnectionConstants';

import ConnectionDetail from './models/Izenda.ConnectionDetail';

export default class ConnectionStore {

  tempExcelFileConnectionStrings = [];

  /**
   * Current connection detail
   * @type {ConnectionDetail}
   */
  @observable item = new ConnectionDetail();

  /**
   * Connection details list
   * @type {ConnectionDetail[]}
   */
  @observable list = [];

  /**
   * Connection filter string
   * @type {string}
   */
  @observable filter = '';

  /**
   * Collection of DB types
   * @type {Object[]}
   */
  @observable dbTypeList = [];

  /**
   * this.list change handler that moves current connection to next eligible connection in the list
   * whenever the list changed.
   */
  dispose = observe(this.list, () => this.nextConnection(this.item ? this.item.id : null));

  /**
   * Whenever uiState changed the tenant
   */
  disposeChangeTenant = observe(uiState, 'tenantId', change => {
    if (uiState.silentUpdate)
      return;
    this.loadList(change)
      .then(() => {
        if (this.list && this.list.length > 0)
          this.setConnectionDetail(this.list[0]);
        return null;
      });
  });

  /**
   * All connections matching filter regexp
   * @returns {ConnectionDetail[]}
   */
  @computed get filteredConnections() {
    const filterRegex = new RegExp(escapeRegExp(this.filter), 'i');
    return uniq(filter(item => item.id && filterRegex.test(item.name), this.list));
  }

  /**
   * Current tenant id
   * @returns {string}
   */
  @computed get tenantId() {
    return uiState.getTenantId();
  }

  /**
   * Set new filter value for connection list.
   * @param {string} newFilter Searching text
   */
  setFilter(newFilter) {
    this.filter = newFilter || '';
  }

  /**
   * Set connection detail
   * @param {ConnectionDetail} detail
   */
  @action setConnectionDetail(detail) {
    if (isNil(this.item.id)) {
      const idx = this.list.indexOf(this.item);
      if (idx >= 0)
        this.list.splice(idx, 1);
    }

    this.item = detail;
  }

  /**
   * Read connection list and map connection data to connection detail instance.
   * That keeps all item in list will be instance of connection detail. It also
   * updates the observable connection list, then the UI will be updated accordingly.
   * @param  {Object[]} connList Array of connection list that returned from API
   */
  @action readConnectionList(connList) {
    // Populate connection detail item instance from its data
    const createConnectionDetail = itemData => {
      const item = new ConnectionDetail();
      item.readDetail(itemData);
      return item;
    };
    this.list = map(createConnectionDetail, connList);
  }

  /**
   * Find db type by id
   * @param {string} dbTypeId db type id
   * @returns {Object} db type
   */
  findDbTypeInList(dbTypeId) {
    if (!dbTypeId)
      return null;
    return this.dbTypeList.find(x => x.id === dbTypeId) || null;
  }

  /**
   * Read database type list. It provides list of db type options to connection
   * detail UI. It will reload full database type list in current store.
   * @param  {Object[]} dbTypeList The database type list that returns from API
   */
  @action readDbTypeList(dbTypeList) {
    this.dbTypeList = dbTypeList;
  }

  /**
   * Add new connection and mark it as selected item.
   * @param {string} id New item id.
   */
  @action addConnection(id) {
    this.item = new ConnectionDetail();
    this.item.id = id;
    return Promise.resolve(this.item);
  }

  /**
   * Move to next eligible connection in showing list.
   * @param {string} startId
   */
  @action nextConnection(startId) {
    if ((isNil(this.item) || isNil(this.item.id)) && isNil(startId)) {

      // TODO
      if (isEmpty(this.list)) {
        // Case of intial loading the empty list, a new connection detail is active
        // in order to allow adding new connection.
        this.addConnection();
      } else {
        // Case of first loading (active item is undefined), move to first connection
        this.item = head(this.list);
      }
    } else if (this.item.id !== startId) {
      const next = find(['id', startId], this.filteredConnections) || find(['id', startId], this.list);
      if (isNil(next)) {
        this.addConnection();
      } else {
        this.item = next;
      }
    }
  }

  /**
   * Load all connections and supported db types from API
   * @param {string} tenantId
   * @return {Promise} A promise of loading connection list
   */
  @action loadList(tenantId) {
    const tenant = isEmpty(tenantId) ? this.tenantId : tenantId;

    return Promise.join(
      dataFetcher.getAllConnectionString(tenant),
      dataFetcher.getSupportedDatabaseType(),
      action((connectionList, dbTypeList) => {
        // Defer notifying changes until reading finished
        this.readDbTypeList(dbTypeList);
        this.readConnectionList(connectionList);
        return null;
      })
    ).then(() => {
      if (isEmpty(this.list)) {
        this.addConnection();
      }
      return null;
    });
  }

  /**
   * Update toggled visibility of connection item
   * @param  {ConnectionDetail} item
   */
  @action updateToggleVisibility(item) {
    const updateVisibility = item.visible ? dataFetcher.invisible : dataFetcher.visible;

    return updateVisibility(item.id)
      .then(action(() => {
          uiState.increaseDetectChangeVisibleConnection();
          item.toggleVisibility();
          return Promise.resolve();
        })
      );
  }

  /**
   * Delete a connection. Side effect of deletion is moving selected item
   * in the list to next one.
   * @param  {Object} delItem Deleted connection
   */
  @action deleteConnection(delItem) {

    // Find next available connection in the list by round-robin ordering pattern
    const findNextId = (availableList, fullList, current) => {
      const nextEligible = list => {
        if (delItem.id === current.id && list.length > 1) {
          const index = findIndex(['id', delItem.id], list);
          // If deleted item is the last item, then take first one, but the next one.
          return index === list.length - 1 ? list[0].id : list[index + 1].id;
        } else if (delItem.id !== current.id) {
          return current.id;
        }
        return null;
      };
      return nextEligible(availableList) || nextEligible(fullList);
    };

    return dataFetcher
      .delete(delItem.id)
      .then(action(() => {
        uiState.increaseDetectChangeVisibleConnection();
        // Select next item in filtered connection list
        const nextId = findNextId(this.filteredConnections, this.list, this.item);
        this.nextConnection(nextId);

        // Delete connection detail instance in the list
        const idx = findIndex(['id', delItem.id], this.list);
        this.list.splice(idx, 1);
        return null;
      }))
      .then(() => {
        // only notify ContextMenu if this connection has changes
        if (delItem.change !== constants.DETECTED_CHANGE_LEVEL.NONE) {
          uiState.increaseDetectChangeCount();
        }
        return null;
      });
  }

  /**
   * Add a new connection to list.
   * @param {Object} connectionDetail Added connection
   */
  @action addConnectionToList(connectionDetail) {
    this.list.push(connectionDetail);
  }

  /**
   * Add temp excel files to delete queue
   * @param {string} filename
   */
  addTempExcelFileToDeleteQueue(filename) {
    if (!this.tempExcelFileConnectionStrings)
      this.tempExcelFileConnectionStrings = [];
    this.tempExcelFileConnectionStrings.push(filename);
  }

  /**
   * delete temp excel files
   */
  @action deleteTempExcelFiles() {
    if (!this.tempExcelFileConnectionStrings)
      return;
    const tempExcelFileConnectionStrings = {
      ConnectionStrings: this.tempExcelFileConnectionStrings
    };
    dataFetcher.deleteTempExcelFiles(tempExcelFileConnectionStrings);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ConnectionStore.js